import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { delay } from "./mock";
import { RESELLERS, WHOLESALERS } from "./seed";
import { storeState, useDemoStore } from "./store";
import { matchSearch } from "./mock";
import { isDemoMode } from "./demo-mode";
import { productApi, orderApi, connectionApi, adminApi, type DirectoryWholesaler } from "./api";
import {
  orderCommission,
  orderTotal,
  stockStatus,
  totalUnits,
  type AuditEntry,
  type BestSeller,
  type DailyPoint,
  type MarketTrend,
  type Order,
  type Product,
  type ReorderAlert,
  type SizeRunout,
} from "@/types/domain";
import { DEMO_NOW } from "./seed";
import { useAuthStore } from "@/stores/auth.store";

/*
  Central data-access layer. Each hook branches on isDemoMode():
    - demo:  read from the reactive demo store (seed data), with a small delay
             so loading states render, and refetch on store mutations.
    - live:  call the backend API via the typed endpoint functions in api.ts.

  The rich analytics/market hooks compute their results from the live orders +
  products, so the frontend keeps its detailed charts even though the backend
  only exposes summary endpoints.
*/

/**
 * Subscribe the query cache to store mutations (demo mode only). In live mode
 * this is a no-op; realtime refresh comes from the SSE subscription instead.
 */
export function useLiveData() {
  const qc = useQueryClient();
  useEffect(() => {
    if (!isDemoMode()) return;
    return useDemoStore.subscribe((state, prev) => {
      if (state.version !== prev.version) {
        qc.invalidateQueries();
      }
    });
  }, [qc]);
}

export const keys = {
  products: (wid?: string, f?: unknown) => ["products", wid, f] as const,
  product: (id: string) => ["product", id] as const,
  orders: (scope: string, id?: string, f?: unknown) => ["orders", scope, id, f] as const,
  wholesalers: (f?: unknown) => ["wholesalers", f] as const,
  resellers: () => ["resellers"] as const,
  audit: () => ["audit"] as const,
};

// ── Products ──

export interface ProductFilter {
  search?: string;
  category?: "sneakers" | "official" | "casual" | "lifestyle";
  status?: "in_stock" | "low_stock" | "out_of_stock";
  visibleOnly?: boolean;
}

function filterProducts(rows: Product[], f: ProductFilter): Product[] {
  let out = rows;
  if (f.visibleOnly) out = out.filter((p) => p.visible);
  if (f.search) out = out.filter((p) => matchSearch([p.name, p.brand, p.wholesalerName], f.search));
  if (f.category) out = out.filter((p) => p.category === f.category);
  if (f.status) out = out.filter((p) => stockStatus(p) === f.status);
  return out;
}

function sortByUpdated(rows: Product[]): Product[] {
  return [...rows].sort((a, b) => +new Date(b.updatedAt) - +new Date(a.updatedAt));
}

/** Products for one wholesaler (their own portal). */
export function useWholesalerProducts(wholesalerId: string, filter: ProductFilter = {}) {
  return useQuery({
    queryKey: keys.products(wholesalerId, filter),
    queryFn: async () => {
      if (isDemoMode()) {
        return delay(
          sortByUpdated(
            filterProducts(
              storeState().products.filter((p) => p.wholesalerId === wholesalerId),
              filter,
            ),
          ),
        );
      }
      // Live: the wholesaler's own products come from GET /products.
      const rows = await productApi.list();
      return sortByUpdated(filterProducts(rows, filter));
    },
  });
}

/** The live cross-network feed (reseller portal) — visible products only. */
export function useNetworkFeed(filter: ProductFilter = {}) {
  return useQuery({
    queryKey: keys.products("network", filter),
    queryFn: async () => {
      if (isDemoMode()) {
        return delay(
          sortByUpdated(filterProducts(storeState().products, { ...filter, visibleOnly: true })),
        );
      }
      // Live: fetch products + wholesaler directory in parallel so we can
      // hydrate wholesalerName/wholesalerMarket (not returned on product rows).
      const [rows, wholesalers] = await Promise.all([
        productApi.list(),
        connectionApi.listWholesalers(),
      ]);
      const whMap = new Map(wholesalers.map((w) => [w.id, w]));
      const hydrated = rows.map((p) => {
        if (p.wholesalerName) return p;
        const wh = whMap.get(p.wholesalerId);
        if (!wh) return p;
        return { ...p, wholesalerName: wh.businessName, wholesalerMarket: wh.market };
      });
      return sortByUpdated(filterProducts(hydrated, filter));
    },
  });
}

export function useProduct(id: string) {
  return useQuery({
    queryKey: keys.product(id),
    queryFn: async () => {
      if (isDemoMode()) return delay(storeState().products.find((p) => p.id === id) ?? null);
      return productApi.get(id);
    },
    enabled: !!id,
  });
}

// ── Orders ──

export function useWholesalerOrders(wholesalerId: string, status?: string) {
  return useQuery({
    queryKey: keys.orders("wholesaler", wholesalerId, status),
    queryFn: async () => {
      if (isDemoMode()) {
        return delay(
          storeState().orders.filter(
            (o) => o.wholesalerId === wholesalerId && (!status || o.status === status),
          ),
        );
      }
      const rows = await orderApi.list();
      return rows.filter((o) => !status || o.status === status);
    },
  });
}

export function useResellerOrders(resellerId: string, status?: string) {
  return useQuery({
    queryKey: keys.orders("reseller", resellerId, status),
    queryFn: async () => {
      if (isDemoMode()) {
        return delay(
          storeState().orders.filter(
            (o) => o.resellerId === resellerId && (!status || o.status === status),
          ),
        );
      }
      const rows = await orderApi.list();
      return rows.filter((o) => !status || o.status === status);
    },
  });
}

// ── Wholesalers / connections (reseller portal) ──
// The backend has no wholesaler-directory endpoint yet, so in live mode we
// derive the connected wholesalers from the visible product feed.

export function useWholesalers(search?: string) {
  return useQuery({
    queryKey: keys.wholesalers(search),
    queryFn: async () => {
      if (isDemoMode()) {
        return delay(WHOLESALERS.filter((w) => matchSearch([w.name, w.market], search)));
      }
      const members = await adminApi.listMembers();
      return members
        .filter((m) => m.role === "wholesaler" && matchSearch([m.businessName, m.market], search))
        .map((m) => ({
          id: m.id,
          name: m.businessName,
          market: m.market || "—",
          plan: (m.subscriptionTier === "business" ? "Business" : m.subscriptionTier === "pro" ? "Pro" : "Free") as "Free" | "Pro" | "Business",
          productCount: 0,
          connected: m.isActive,
          lastActiveAt: m.updatedAt,
          rating: 4.5,
        }));
    },
  });
}

/**
 * Reseller-facing wholesaler directory with real connection state. In demo mode
 * it adapts the seeded WHOLESALERS list to the same shape.
 */
export function useWholesalerDirectory(search?: string) {
  return useQuery({
    queryKey: ["wholesaler-directory", search, isDemoMode()],
    queryFn: async (): Promise<DirectoryWholesaler[]> => {
      if (isDemoMode()) {
        return WHOLESALERS.filter((w) => matchSearch([w.name, w.market], search)).map((w) => ({
          id: w.id,
          businessName: w.name,
          market: w.market,
          tier: w.plan,
          productCount: w.productCount,
          connected: w.connected,
        }));
      }
      return connectionApi.listWholesalers(search);
    },
  });
}

export function useResellers() {
  return useQuery({
    queryKey: keys.resellers(),
    queryFn: async () => {
      if (isDemoMode()) return delay(RESELLERS);
      const members = await adminApi.listMembers();
      return members
        .filter((m) => m.role === "reseller")
        .map((m) => ({
          id: m.id,
          name: m.businessName,
          plan: (m.subscriptionTier === "growth" ? "Growth" : m.subscriptionTier === "starter" ? "Starter" : "Free") as "Free" | "Starter" | "Growth",
          connections: 0,
          gmvMonth: 0,
          commissionRate: 0.04,
          joinedAt: m.createdAt,
        }));
    },
  });
}

export function useAudit(params?: { table?: string; action?: string }) {
  return useQuery({
    queryKey: [...keys.audit(), params],
    queryFn: async () => {
      if (isDemoMode()) return delay(storeState().audit);
      const entries = await adminApi.getAudit({ ...params, limit: 200 });
      return entries.map((e) => ({
        id: e.id,
        actor: e.changedBy,
        actorRole: (["wholesaler", "reseller", "admin"].includes(e.actorRole)
          ? e.actorRole
          : "system") as AuditEntry["actorRole"],
        action: e.operation,
        target: e.tableName,
        recordId: e.recordId,
        oldData: e.oldData,
        newData: e.newData,
        at: e.changedAt,
      }));
    },
  });
}

// ── Derived analytics (computed from orders) ──

async function ordersForAnalytics(wholesalerId?: string): Promise<Order[]> {
  const orders = isDemoMode() ? storeState().orders : await orderApi.list();
  const rows = wholesalerId ? orders.filter((o) => o.wholesalerId === wholesalerId) : orders;
  return rows.filter((o) => o.status !== "cancelled");
}

async function productsForAnalytics(wholesalerId: string): Promise<Product[]> {
  const rows = isDemoMode() ? storeState().products : await productApi.list();
  return rows.filter((p) => p.wholesalerId === wholesalerId);
}

export interface AnalyticsResult {
  totalRevenue: number;
  unitsSold: number;
  avgSale: number;
  turnoverRate: number;
  daily: DailyPoint[];
  bestSellers: BestSeller[];
  sizeHeatmap: SizeRunout[];
  reorder: ReorderAlert[];
}

export function useWholesalerAnalytics(wholesalerId: string, days = 30) {
  return useQuery({
    queryKey: ["analytics", wholesalerId, days, isDemoMode()],
    queryFn: () => computeAnalytics(wholesalerId, days),
  });
}

async function computeAnalytics(wholesalerId: string, days: number): Promise<AnalyticsResult> {
  // In demo mode use the frozen DEMO_NOW so the seeded series lines up; live
  // mode uses the real clock.
  const nowMs = isDemoMode() ? DEMO_NOW.getTime() : Date.now();
  const cutoff = nowMs - days * 86_400_000;
  const allRows = await ordersForAnalytics(wholesalerId);
  const rows = allRows.filter((o) => +new Date(o.createdAt) >= cutoff);

  const totalRevenue = rows.reduce((s, o) => s + orderTotal(o), 0);
  const unitsSold = rows.reduce((s, o) => s + o.qty, 0);
  const avgSale = rows.length ? Math.round(totalRevenue / rows.length) : 0;

  const byDay = new Map<string, DailyPoint>();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(nowMs - i * 86_400_000);
    const key = d.toISOString().slice(0, 10);
    byDay.set(key, { date: key, revenue: 0, units: 0 });
  }
  for (const o of rows) {
    const key = new Date(o.createdAt).toISOString().slice(0, 10);
    const p = byDay.get(key);
    if (p) {
      p.revenue += orderTotal(o);
      p.units += o.qty;
    }
  }

  const bsMap = new Map<string, BestSeller>();
  for (const o of rows) {
    const b = bsMap.get(o.productName) ?? { productId: o.productId, name: o.productName, brand: o.brand, units: 0, revenue: 0 };
    b.units += o.qty;
    b.revenue += orderTotal(o);
    bsMap.set(o.productName, b);
  }
  const bestSellers = [...bsMap.values()].sort((a, b) => b.units - a.units).slice(0, 6);

  const HEATMAP_SIZES = ["38", "39", "40", "41", "42", "43", "44", "45"];
  const sizeMap = new Map<string, number>(HEATMAP_SIZES.map((s) => [s, 0]));
  for (const o of rows) {
    // Normalise "UK 42" → "42" so live sizes bucket into the heatmap grid.
    const key = o.size.replace(/[^0-9]/g, "");
    if (sizeMap.has(key)) sizeMap.set(key, (sizeMap.get(key) ?? 0) + o.qty);
  }
  const maxSize = Math.max(1, ...sizeMap.values());
  const sizeHeatmap: SizeRunout[] = [...sizeMap.entries()]
    .map(([size, units]) => ({ size, demandIndex: Math.round((units / maxSize) * 100) }))
    .sort((a, b) => a.size.localeCompare(b.size, undefined, { numeric: true }));

  const products = await productsForAnalytics(wholesalerId);
  const reorder: ReorderAlert[] = products
    .map((p) => {
      const sold = rows.filter((o) => o.productId === p.id).reduce((s, o) => s + o.qty, 0);
      const velocity = sold / days || 0.1;
      const stock = totalUnits(p);
      const daysLeft = Math.round(stock / velocity);
      const status = stockStatus(p);
      let urgency: ReorderAlert["urgency"] = daysLeft <= 3 ? "now" : daysLeft <= 10 ? "soon" : "healthy";
      if (status === "out_of_stock") urgency = "now";
      else if (status === "low_stock" && urgency === "healthy") urgency = "soon";
      const lowest = [...p.sizes].sort((a, b) => a.qty - b.qty)[0];
      return { productId: p.id, name: `${p.brand} ${p.name}`, size: lowest?.size ?? "—", daysLeft, urgency };
    })
    .filter((r) => r.urgency !== "healthy")
    .sort((a, b) => a.daysLeft - b.daysLeft)
    .slice(0, 6);

  const onHand = products.reduce((s, p) => s + totalUnits(p), 0);
  const turnoverRate = onHand > 0 ? Number((unitsSold / onHand).toFixed(2)) : 0;

  return { totalRevenue, unitsSold, avgSale, turnoverRate, daily: [...byDay.values()], bestSellers, sizeHeatmap, reorder };
}

// ── Reseller commission summary ──

export function useResellerCommissions(resellerId: string) {
  return useQuery({
    queryKey: ["commissions", resellerId, isDemoMode()],
    queryFn: async () => {
      const orders = isDemoMode() ? storeState().orders : await orderApi.list();
      const rows = orders.filter((o) => o.resellerId === resellerId && o.status !== "cancelled");
      const gmv = rows.reduce((s, o) => s + orderTotal(o), 0);
      const commission = rows.reduce((s, o) => s + orderCommission(o), 0);
      return { gmv, commission, orders: rows.length, rate: rows[0]?.commissionRate ?? 0.03 };
    },
  });
}

// ── Admin network stats + market intelligence ──

export function useNetworkStats() {
  return useQuery({
    queryKey: ["network-stats", isDemoMode()],
    queryFn: async () => {
      if (isDemoMode()) {
        const orders = storeState().orders;
        const products = storeState().products;
        const active = orders.filter((o) => o.status !== "cancelled");
        const gmv = active.reduce((s, o) => s + orderTotal(o), 0);
        const commission = active.reduce((s, o) => s + orderCommission(o), 0);
        const stale = WHOLESALERS.filter((w) => DEMO_NOW.getTime() - +new Date(w.lastActiveAt) > 2 * 86_400_000).length;
        return {
          wholesalers: WHOLESALERS.length,
          resellers: RESELLERS.length,
          products: products.length,
          gmv,
          commission,
          orders: active.length,
          staleWholesalers: stale,
        };
      }
      const [overview, orders, products] = await Promise.all([
        adminApi.getOverview(),
        orderApi.list(),
        productApi.list(),
      ]);
      const active = orders.filter((o) => o.status !== "cancelled");
      const gmv = active.reduce((s, o) => s + orderTotal(o), 0);
      const commission = active.reduce((s, o) => s + orderCommission(o), 0);
      return {
        wholesalers: overview.totalWholesalers,
        resellers: overview.totalResellers,
        products: products.length,
        gmv,
        commission,
        orders: active.length,
        staleWholesalers: 0,
      };
    },
  });
}

export function useMarketTrends() {
  return useQuery({
    queryKey: ["market-trends", isDemoMode()],
    queryFn: async () => {
      const orders = isDemoMode() ? storeState().orders : await orderApi.list();
      const rows = orders.filter((o) => o.status !== "cancelled");
      const map = new Map<string, MarketTrend>();
      for (const o of rows) {
        const key = `${o.brand} ${o.productName}`;
        const t = map.get(key) ?? { model: o.productName, brand: o.brand, unitsSold: 0, weekChange: 0 };
        t.unitsSold += o.qty;
        map.set(key, t);
      }
      return [...map.values()]
        .map((t, i) => ({ ...t, weekChange: Math.round((seedChange(t.model) - 0.4) * 60) - (i % 3) }))
        .sort((a, b) => b.unitsSold - a.unitsSold)
        .slice(0, 8);
    },
  });
}

function seedChange(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 1000;
  return h / 1000;
}

/** The authenticated user's id, used by pages that key queries off "me". */
export function useCurrentUserId(): string {
  return useAuthStore((s) => s.user?.id ?? "");
}

/** Network-wide daily GMV series (admin market intelligence chart). */
export function useNetworkDailyStats(days = 30) {
  return useQuery({
    queryKey: ["network-daily", days, isDemoMode()],
    queryFn: async (): Promise<DailyPoint[]> => {
      const orders = isDemoMode() ? storeState().orders : await orderApi.list();
      const nowMs = isDemoMode() ? DEMO_NOW.getTime() : Date.now();
      const cutoff = nowMs - days * 86_400_000;
      const active = orders.filter((o) => o.status !== "cancelled" && +new Date(o.createdAt) >= cutoff);

      const byDay = new Map<string, DailyPoint>();
      for (let i = days - 1; i >= 0; i--) {
        const d = new Date(nowMs - i * 86_400_000);
        const key = d.toISOString().slice(0, 10);
        byDay.set(key, { date: key, revenue: 0, units: 0 });
      }
      for (const o of active) {
        const key = new Date(o.createdAt).toISOString().slice(0, 10);
        const p = byDay.get(key);
        if (p) { p.revenue += orderTotal(o); p.units += o.qty; }
      }
      return [...byDay.values()];
    },
  });
}
